import { useCollectionLocalStore } from "./use-collection-local-store";
import { useCollectionRemoteStore } from "./use-collection-remote-store";
import { useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { v4 as uuid } from "uuid";
import { TemplateCollection } from "PosterEditor/model/template-collection";
import { ProjectTemplate } from "PosterEditor/model/project-template";
import { useTemplateStore } from "entity-store/template-store/use-template-store";

export const useCollectionStore = () => {
  const queryClient = useQueryClient();

  const { loadTemplatesByIDs } = useTemplateStore();
  const {
    loadCollections: loadCollectionsLocal,
    loadCollection: loadCollectionLocal,
    saveCollection: saveCollectionLocal,
    createCollection: createCollectionLocal,
    deleteCollection: deleteCollectionLocal,
    deleteAllCollections: deleteAllCollectionsLocal,
  } = useCollectionLocalStore();
  const {
    save: saveRemote,
    fetchAllCollections: fetchAllRemote,
    fetchCollectionByID: fetchCollectionByIDRemote,
    remove: removeRemote,
  } = useCollectionRemoteStore();

  const {
    data: collections,
    refetch: refetchCollections,
    isLoading,
    isFetching,
    isError,
  } = useQuery({
    queryKey: ["collections"],
    queryFn: async () => {
      const remoteData = await fetchAllRemote();
      clearLocalCache();
      if (remoteData.length) {
        remoteData.forEach((remoteCollection) => {
          saveCollectionLocal(remoteCollection);
        });
      }
      const data = await loadCollectionsLocal();
      return data ?? [];
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });

  const loadCollection = async (collectionID: string) => {
    //TODO: fetch from remote if needed - we could be loading from an ID in the URL.
    const collection = await loadCollectionLocal(collectionID);
    return collection;
  };

  const saveCollection = async (collection: TemplateCollection) => {
    try {
      const updated: TemplateCollection = {
        ...collection,
        updatedAt: new Date(),
      };

      // 🔥 Save only updated version everywhere
      await saveCollectionLocal(updated);

      // 🔥 Optimistically update the cache with correct order
      queryClient.setQueryData(
        ["collections"],
        (oldData?: TemplateCollection[]) => {
          if (!oldData) return [updated];
          return oldData.map((c) => (c.id === updated.id ? updated : c));
        }
      );

      // 🔥 Push to remote
      await saveRemote.mutateAsync(updated);

      // 🛑 Optionally remove this if remote data comes back out-of-order
      // queryClient.invalidateQueries({ queryKey: ["collections"] });
    } catch (err) {
      console.error("Save failed:", err);
      throw err;
    }
  };
  //TODO: proper implementation when backend is up.
  const loadTemplatesForCollection = async (
    collection: TemplateCollection
  ): Promise<ProjectTemplate[]> => {
    return loadTemplatesByIDs(collection.templateIDs);
  };

  //TODO: proper implementation when backend is up.
  const putCollection = useMutation({
    mutationFn: async (collection: TemplateCollection) =>
      await saveRemote.mutateAsync(collection),
  });

  const createCollection = useMutation({
    mutationFn: async () => {
      const collection = await createCollectionLocal();
      await putCollection.mutateAsync(collection);
      return;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["collections"] });
    },
  });

  const deleteCollection = useMutation({
    mutationFn: async (collection: TemplateCollection) => {
      await deleteCollectionLocal(collection);
      await removeRemote.mutateAsync(collection.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["collections"] });
    },
  });

  const clearLocalCache = () => {
    deleteAllCollectionsLocal();
  };

  return {
    collections, // Cached projects list.
    loadCollection,
    loadTemplatesForCollection,
    saveCollection,
    putCollection,
    createCollection,
    deleteCollection,
    refetchCollections,
    loadCollectionByID: fetchCollectionByIDRemote,
    isLoading,
    isFetching,
    isError,
  };
};
